import React, { useState } from 'react';
import { X, FileDown, Lock, Unlock, Loader2 } from 'lucide-react';
import { useStore } from '../store/useStore';
import { exportWorkflowBasic, exportWorkflowPremium } from '../utils/pdfExport';

interface ExportPDFDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ExportPDFDialog: React.FC<ExportPDFDialogProps> = ({ isOpen, onClose }) => {
  const { getCurrentWorkflow, isUnlocked } = useStore();
  const [customBranding, setCustomBranding] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const workflow = getCurrentWorkflow();

  if (!isOpen) return null;

  const handleExport = async () => {
    if (!workflow) {
      setExportError('No workflow selected');
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      if (isUnlocked) {
        await exportWorkflowPremium(workflow, customBranding || undefined);
      } else {
        await exportWorkflowBasic(workflow);
      }

      // Success - close dialog after brief delay
      setTimeout(() => {
        setIsExporting(false);
        onClose();
      }, 500);
    } catch (error) {
      console.error('PDF export failed:', error);
      setExportError(error instanceof Error ? error.message : 'Failed to export PDF');
      setIsExporting(false);
    }
  };

  const handleGetLicense = () => {
    const checkoutUrl = import.meta.env.VITE_LEMON_SQUEEZY_CHECKOUT_URL;

    if (checkoutUrl && checkoutUrl !== 'https://store.lemonsqueezy.com/checkout') {
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert(
        'Checkout URL not configured.\\n\\n' +
        'Please set VITE_LEMON_SQUEEZY_CHECKOUT_URL in .env.local\\n\\n' +
        'See LEMON_SQUEEZY_SETUP.md for instructions.'
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <FileDown className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Export to PDF</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Workflow Info */}
          <div>
            <p className="text-sm text-muted-foreground mb-1">Workflow</p>
            <p className="font-medium">{workflow?.title || 'Untitled Workflow'}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {workflow?.nodes.length || 0} steps
            </p>
          </div>

          {/* Export Features */}
          <div className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-emerald-500/20 text-emerald-600">
                ✓
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Workflow Visualization</p>
                <p className="text-xs text-muted-foreground">High-quality graph screenshot</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-emerald-500/20 text-emerald-600">
                ✓
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Step Details Table</p>
                <p className="text-xs text-muted-foreground">Complete step list with apps</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                isUnlocked ? 'bg-emerald-500/20 text-emerald-600' : 'bg-muted text-muted-foreground'
              }`}>
                {isUnlocked ? '✓' : <Lock className="w-3 h-3" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  Configuration Details
                  {!isUnlocked && <span className="ml-1 text-xs text-amber-600">• Pro Only</span>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isUnlocked ? 'All field mappings and settings' : 'Requires Pro license'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                isUnlocked ? 'bg-emerald-500/20 text-emerald-600' : 'bg-muted text-muted-foreground'
              }`}>
                {isUnlocked ? '✓' : <Lock className="w-3 h-3" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  Custom Branding
                  {!isUnlocked && <span className="ml-1 text-xs text-amber-600">• Pro Only</span>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isUnlocked ? 'Add your company name' : 'Upgrade to customize'}
                </p>
              </div>
            </div>
          </div>

          {/* Custom Branding Input (Premium Only) */}
          {isUnlocked && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Custom Branding (Optional)
              </label>
              <input
                type="text"
                value={customBranding}
                onChange={(e) => setCustomBranding(e.target.value)}
                placeholder="e.g., Your Company Name"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isExporting}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Appears in PDF footer. Leave blank for default.
              </p>
            </div>
          )}

          {/* Error Message */}
          {exportError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{exportError}</p>
            </div>
          )}

          {/* Freemium Upsell (if not unlocked) */}
          {!isUnlocked && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Unlock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    Upgrade for Full PDF Export
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Get configuration details, custom branding, and remove watermarks
                  </p>
                </div>
              </div>
              <button
                onClick={handleGetLicense}
                className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Get Pro License - $29
              </button>
              <p className="text-xs text-amber-700 dark:text-amber-300 text-center">
                Lifetime access • Pay once, use forever
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border bg-muted/30">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {isUnlocked ? (
              <>
                <Unlock className="w-3.5 h-3.5" />
                <span>Pro Export</span>
              </>
            ) : (
              <>
                <Lock className="w-3.5 h-3.5" />
                <span>Free Export</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg hover:bg-accent transition-colors text-sm"
              disabled={isExporting}
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isExporting || !workflow}
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4" />
                  Export PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
